import type { ReactElement } from 'react';

export interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  id?: string;
  /** Required unless a visible label points at the switch (aria-labelledby / <label htmlFor>). */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  /** Busy while the change is saving. */
  busy?: boolean;
  className?: string;
  testId?: string;
  title?: string;
}

/**
 * The app's one on/off switch: a 40×24 track inside a 44×44 hit area
 * (touch target), aubergine when on, line-2 when off, knob on the inline
 * start/end so it mirrors in RTL. role="switch" with aria-checked.
 */
export function Switch({
  checked,
  onCheckedChange,
  disabled = false,
  busy = false,
  id,
  className = '',
  testId,
  title,
  ...aria
}: SwitchProps): ReactElement {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={aria['aria-label']}
      aria-labelledby={aria['aria-labelledby']}
      aria-describedby={aria['aria-describedby']}
      aria-busy={busy || undefined}
      disabled={disabled}
      title={title}
      data-testid={testId}
      onClick={() => onCheckedChange(!checked)}
      className={['switch', className].filter(Boolean).join(' ')}
    >
      <span className="switch-track" aria-hidden="true">
        <span className="switch-knob" />
      </span>
    </button>
  );
}
